import React, { useState } from 'react';
import { stats } from '../../util/MetricsConfig'; // Adjust the import path as needed
import { CheckCircleIcon } from "@heroicons/react/20/solid"; // Import icons as needed

const MetricsSelectionForm = ({ onNextStep, onMetricsSelect, onBack }) => {
    const [selectedMetrics, setSelectedMetrics] = useState([]);

    const toggleMetricSelection = (id) => {
        setSelectedMetrics((prevSelected) => {
            if (prevSelected.includes(id)) {
                return prevSelected.filter((metricId) => metricId !== id);
            } else if (prevSelected.length < 3) {
                return [...prevSelected, id];
            }
            return prevSelected;
        });
    };

    const handleNext = () => {
        onMetricsSelect(selectedMetrics);
        onNextStep();
    };

    // Define inline styles for the gradient background
    const gradientBackground = {
        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(169, 168, 243, 0.5))',
    };

    // Calculate the progress as a percentage
    const progressBarWidth = `${(selectedMetrics.length / 3) * 100}%`;

    // Determine the prompt text based on the number of selected metrics
    const textState = selectedMetrics.length === 3
        ? "Great! You've selected 3 metrics."
        : `Please select ${3 - selectedMetrics.length} more.`;

    return (
        <div className="container mx-auto px-2 sm:px-4 lg:px-6 max-w-4xl">
            <h1 className="text-center text-base font-semibold text-gray-700 mb-2">🚀 Choose 3 metrics you want to unlock benchmarks for.</h1>
            <div className="py-2 rounded-t-lg">
                <div className="container mx-auto text-white text-center relative">
                    <div className="h-4 bg-[#cbd5e1] rounded-full mb-1">
                        <div
                            className="h-full rounded-full bg-gradient-to-r from-pink-300 to-indigo-500 transition-all duration-500"
                            style={{ width: progressBarWidth }}
                        ></div>
                    </div>
                    <div className="text-xs text-gray-600">
                        <p className="mb-0.5">{textState}</p>
                        <p className="mb-0.5 font-semibold">{`${selectedMetrics.length}/3 metrics selected`}</p>
                    </div>
                </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto mb-8">
                {stats.map((metric) => (
                    <div 
                        key={metric.id} 
                        style={gradientBackground} 
                        className={`w-full p-2 hover:cursor-pointer bg-white rounded-lg border shadow-sm group relative ${selectedMetrics.includes(metric.id) ? 'border-indigo-500' : 'border-gray-200'}`} 
                        onClick={() => toggleMetricSelection(metric.id)}
                    >
                        <div className="flex flex-col h-[40px]">
                            <div className="flex items-start justify-between gap-2">
                                <div className="flex items-start gap-1.5 flex-1 min-w-0">
                                    {metric.icon && React.createElement(metric.icon, { className: "h-3.5 w-3.5 text-indigo-500 mt-0.5 shrink-0" })}
                                    <h5 className="text-sm font-semibold text-gray-900 leading-tight truncate group-hover:whitespace-normal group-hover:overflow-visible">
                                        {metric.name}
                                    </h5>
                                </div>
                                <div className="flex items-center gap-1.5 shrink-0">
                                    {metric.metricType === 'People' ? (
                                        <span className="inline-flex items-center rounded-md bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-700">
                                            {metric.metricType}
                                        </span>
                                    ) : metric.metricType === 'Talent' ? (
                                        <span className="inline-flex items-center rounded-md bg-purple-50 px-1.5 py-0.5 text-[10px] font-medium text-purple-700">
                                            {metric.metricType}
                                        </span>
                                    ) : null}
                                    {selectedMetrics.includes(metric.id) && <CheckCircleIcon className="h-4 w-4 text-indigo-500" />}
                                </div>
                            </div>
                        </div>
                        <div className="absolute bottom-full left-0 mb-2 hidden group-hover:block bg-gray-900 text-white text-xs rounded py-1 px-2 whitespace-nowrap z-10">
                            {metric.name}
                        </div>
                    </div>
                ))}
            </div>
            <div className="flex justify-center space-x-4 mt-8">
                <button
                    onClick={onBack}
                    className="back-button"
                >
                    Back
                </button>
                <button
                    onClick={handleNext}
                    className="submit-button"
                    disabled={selectedMetrics.length !== 3}
                >
                    🚀 Next
                </button>
            </div>
        </div>
    );
};

export default MetricsSelectionForm;
